import { css } from '@styled-system/css';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AnalysisMenuLoadingState } from '@/features/analysisMenu/components/AnalysisMenuLoadingState';
import { usePublicAnalysisMenuListData } from '@/features/analysisMenu/hooks/usePublicAnalysisMenuListData';
import { useAnalysisMenuStore } from '@/features/analysisMenu/stores/analysis-menu.store';
import { AnalysisItemData } from '@/features/analysisMenu/types';
import { Logo } from '@/shared/components/ui/Logo';
import { useLastVisitedAnalysis } from '@/shared/hooks/analysis/useLastVisitedAnalysis';

import { AnalysisMenuHeader } from './AnalysisMenuHeader';
import { AnalysisMenuItem } from './AnalysisMenuItem';

interface PublicAnalysisMenuListProps {
  onAnalysisClick: (analysis: AnalysisItemData) => void;
  selectedAnalysisId?: string;
  onAutoRedirect?: (analysisId: string) => void;
  isLoading?: boolean;
  noSubscriptionActive?: boolean;
  navigateToSubscription?: () => void;
}

export const PublicAnalysisMenuList: FC<PublicAnalysisMenuListProps> = ({
  onAnalysisClick,
  selectedAnalysisId,
  onAutoRedirect,
  isLoading: parentIsLoading,
  noSubscriptionActive,
  navigateToSubscription,
}) => {
  const { t } = useTranslation();

  // Hook pour la navigation
  const navigate = useNavigate();

  // Récupération des données via le hook
  const { categories, loading: dataIsLoading, error } = usePublicAnalysisMenuListData();

  // Store pour l'état partagé
  const { isLoading, searchQuery, setIsLoading, setIsEmpty, setError, setSearchQuery } = useAnalysisMenuStore();

  // Hook pour la dernière analyse visitée
  const { getLastVisited, setLastVisited } = useLastVisitedAnalysis();

  // Synchroniser l'état du hook avec le store
  useEffect(() => {
    setIsLoading(parentIsLoading || dataIsLoading);
    setIsEmpty(categories.length === 0);
    setError(error ? t('analysisMenu.loadError') : null);
    setSearchQuery(''); // Réinitialiser la recherche à chaque chargement
  }, [parentIsLoading, dataIsLoading, categories.length, error, setIsLoading, setIsEmpty, setError, setSearchQuery, t]);

  // Auto-redirection vers la première analyse publique disponible
  useEffect(() => {
    if (!isLoading && categories.length > 0 && !selectedAnalysisId && onAutoRedirect) {
      // Récupérer toutes les analyses disponibles avec ID valide
      const allAnalyses = categories.flatMap((category) => category.analyses).filter((analysis) => analysis.id !== '');

      if (allAnalyses.length === 0) return;

      // Priorité 1: Dernière analyse visitée
      const lastVisited = getLastVisited('public');
      const lastVisitedAnalysis = allAnalyses.find((analysis) => analysis.id === lastVisited);

      if (lastVisitedAnalysis) {
        // Trouver la catégorie qui contient cette analyse
        const categoryWithAnalysis = categories.find((category) =>
          category.analyses.some((analysis) => analysis.id === lastVisitedAnalysis.id),
        );
        if (categoryWithAnalysis) {
          setExpandedCategories((prev) => new Set([...prev, categoryWithAnalysis.id]));
        }
        onAutoRedirect(lastVisitedAnalysis.id);
        return;
      }

      // Priorité 2: Première analyse avec ID valide
      const firstValidAnalysis = allAnalyses[0];
      if (firstValidAnalysis) {
        // Trouver la catégorie qui contient cette analyse
        const categoryWithAnalysis = categories.find((category) =>
          category.analyses.some((analysis) => analysis.id === firstValidAnalysis.id),
        );
        if (categoryWithAnalysis) {
          setExpandedCategories((prev) => new Set([...prev, categoryWithAnalysis.id]));
        }
        onAutoRedirect(firstValidAnalysis.id);
      }
    }
  }, [isLoading, categories, selectedAnalysisId, onAutoRedirect, getLastVisited]);

  // Étendre automatiquement la catégorie contenant l'analyse sélectionnée
  useEffect(() => {
    if (selectedAnalysisId && categories.length > 0) {
      const categoryWithSelectedAnalysis = categories.find((category) =>
        category.analyses.some((analysis) => analysis.id === selectedAnalysisId),
      );

      if (categoryWithSelectedAnalysis) {
        setExpandedCategories((prev) => new Set([...prev, categoryWithSelectedAnalysis.id]));
      }
    }
  }, [selectedAnalysisId, categories]);

  // Sauvegarder l'analyse sélectionnée
  useEffect(() => {
    if (selectedAnalysisId) {
      setLastVisited('public', selectedAnalysisId);
    }
  }, [selectedAnalysisId, setLastVisited]);

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Gérer le clic sur une analyse
  const handleAnalysisClick = (analysis: AnalysisItemData) => {
    if (analysis.id === '') {
      // Si l'ID est vide (accès partiel), rediriger vers la page d'abonnement
      navigate('/subscription');
    } else {
      // Sinon, utiliser la fonction de clic normale
      onAnalysisClick(analysis);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Filtrer les catégories et analyses par recherche
  const filteredCategories = categories
    .map((category) => ({
      ...category,
      analyses: category.analyses.filter((analysis) => analysis.name.toLowerCase().includes(searchQuery.toLowerCase())),
    }))
    .filter((category) => category.analyses.length > 0);

  // État pour suivre la dernière recherche
  const [lastSearchQuery, setLastSearchQuery] = useState('');

  // Étendre automatiquement les catégories qui contiennent des analyses correspondant à la recherche
  useEffect(() => {
    const currentSearch = searchQuery.trim();
    const previousSearch = lastSearchQuery.trim();

    // Ne pas traiter si la recherche n'a pas changé
    if (currentSearch === previousSearch) {
      return;
    }

    if (currentSearch !== '') {
      // Recherche non vide : étendre toutes les catégories qui ont des résultats
      const categoriesToExpand = filteredCategories.map((category) => category.id);
      setExpandedCategories((prev) => {
        const newSet = new Set(prev);
        categoriesToExpand.forEach((categoryId) => newSet.add(categoryId));
        return newSet;
      });
    } else if (previousSearch !== '') {
      // Passage d'une recherche non vide à une recherche vide : fermer toutes les catégories sauf celle contenant l'analyse sélectionnée
      setExpandedCategories(() => {
        const newSet = new Set<string>();

        // Garder ouverte la catégorie contenant l'analyse sélectionnée
        if (selectedAnalysisId) {
          const categoryWithSelectedAnalysis = categories.find((category) =>
            category.analyses.some((analysis) => analysis.id === selectedAnalysisId),
          );
          if (categoryWithSelectedAnalysis) {
            newSet.add(categoryWithSelectedAnalysis.id);
          }
        }

        return newSet;
      });
    }

    setLastSearchQuery(currentSearch);
  }, [searchQuery, filteredCategories, selectedAnalysisId, categories, lastSearchQuery]);

  const sidebarStyles = css({
    w: 'full',
    h: '100vh',
    bg: 'transparent',
    borderRight: '1px solid',
    borderColor: 'base.segBg',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'relative',
  });

  const contentStyles = css({
    flex: 1,
    overflowY: 'auto',
    px: '0.5625rem', // 9px - même que le menu des analyses utilisateur
  });

  const categoryHeaderStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: 'xs',
    px: 0, // pas de padding horizontal
    py: 0, // pas de padding vertical
    cursor: 'pointer',
    color: 'font.lowkey', // #4f5055
    fontSize: '0.625rem', // 10px
    fontWeight: 'semibold',
    textTransform: 'uppercase',
    transition: 'all 0.15s ease',
    _hover: {
      color: 'font.secondary',
    },
  });

  const analysisListStyles = css({
    pl: 0, // pas d'indentation, alignement à gauche
    mt: '0.3125rem', // 5px
    gap: 0, // pas de gap entre les éléments
    display: 'flex',
    flexDirection: 'column',
  });

  const emptyStateStyles = css({
    textAlign: 'center',
    py: '2rem',
    color: 'font.lowkey', // font-lowkey
    fontSize: '2xs',
  });

  return (
    <div className={sidebarStyles}>
      <AnalysisMenuHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showCreateButton={false}
        noSubscriptionActive={noSubscriptionActive}
        navigateToSubscription={navigateToSubscription}
        isLoading={isLoading}
      />

      <div className={contentStyles}>
        {isLoading ? (
          <AnalysisMenuLoadingState />
        ) : filteredCategories.length === 0 ? (
          <div className={emptyStateStyles}>{t('analysisMenu.noneAvailable')}</div>
        ) : (
          <div className={css({ display: 'flex', flexDirection: 'column', gap: 'lg' })}>
            {filteredCategories.map((category) => {
              const isExpanded = expandedCategories.has(category.id);
              return (
                <div key={category.id}>
                  <div className={categoryHeaderStyles} onClick={() => toggleCategory(category.id)}>
                    <span>{category.name}</span>
                    <Logo
                      variant="arrow"
                      color="font.lowkey"
                      className={css({
                        w: '0.625rem', // 10px
                        h: '0.375rem', // 6px
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 150ms ease',
                        flexShrink: 0,
                      })}
                    />
                  </div>
                  {isExpanded && (
                    <div className={analysisListStyles}>
                      {category.analyses.map((analysis) => (
                        <AnalysisMenuItem
                          key={analysis.id || `empty-${category.id}-${Math.random()}`}
                          analysis={analysis}
                          enableMenu={false}
                          isSelected={selectedAnalysisId === analysis.id}
                          isBlurred={analysis.id === ''}
                          onClick={() => handleAnalysisClick(analysis)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
